import React, { useRef } from 'react';
import { ChevronLeft, ChevronRight, Play, Download, FileText, FileImage, File } from 'lucide-react';
import { Button } from './ui/button';
import { getContentPreview, getContentMediaKind, contentKindLabel } from '../lib/contentPreview';

const CAROUSEL_ITEM_WIDTH = 290;
const NEW_BADGE_DAYS = 7;

const isNew = (createdAt) => {
  if (!createdAt) return false;
  const created = new Date(createdAt).getTime();
  if (Number.isNaN(created)) return false;
  const diffDays = (Date.now() - created) / (1000 * 60 * 60 * 24);
  return diffDays >= 0 && diffDays <= NEW_BADGE_DAYS;
};

const ContentCarousel = ({ title, contents, onContentClick, delay = 0 }) => {
  const scrollRef = useRef(null);

  const scroll = (direction) => {
    if (scrollRef.current) {
      scrollRef.current.scrollBy({
        left: direction === 'left' ? -CAROUSEL_ITEM_WIDTH : CAROUSEL_ITEM_WIDTH,
        behavior: 'smooth'
      });
    }
  };

  return (
    <div
      className="mb-8 opacity-0 animate-fade-in"
      style={{ animationDelay: `${delay}s`, animationFillMode: 'forwards' }}
      data-testid={`carousel-${title.toLowerCase().replace(/\s+/g, '-')}`}
    >
      <div className="flex items-center justify-between mb-4 px-1">
        <h3 className="text-lg font-medium text-zinc-900" style={{ fontFamily: 'Outfit, sans-serif' }}>
          {title}
        </h3>
        <div className="hidden md:flex gap-2">
          <Button variant="ghost" size="icon"
            onClick={() => scroll('left')}
            className="w-9 h-9 rounded-full bg-white border border-zinc-200 hover:bg-zinc-50 text-zinc-700"
            data-testid={`carousel-prev-${title.toLowerCase().replace(/\s+/g, '-')}`}
          >
            <ChevronLeft className="w-4 h-4" />
          </Button>
          <Button variant="ghost" size="icon"
            onClick={() => scroll('right')}
            className="w-9 h-9 rounded-full bg-white border border-zinc-200 hover:bg-zinc-50 text-zinc-700"
            data-testid={`carousel-next-${title.toLowerCase().replace(/\s+/g, '-')}`}
          >
            <ChevronRight className="w-4 h-4" />
          </Button>
        </div>
      </div>

      <div
        ref={scrollRef}
        className="flex gap-4 overflow-x-auto hide-scrollbar snap-x snap-mandatory pb-4 -mx-4 px-4"
      >
        {contents.map((content) => {
          const previewUrl = getContentPreview(content);
          const mediaKind = getContentMediaKind(content);
          const KindIcon = mediaKind === 'video' ? Play : mediaKind === 'pdf' ? FileText : mediaKind === 'image' ? FileImage : File;
          return (
          <div
            key={content.id}
            role="button"
            tabIndex={0}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                onContentClick(content);
              }
            }}
            onClick={(e) => {
              if (e.detail === 0) return;
              e.preventDefault();
              e.stopPropagation();
              onContentClick(content);
            }}
            className="flex-shrink-0 w-64 md:w-72 cursor-pointer group snap-start touch-manipulation"
            data-testid={`content-card-${content.id}`}
          >
            <div className="bg-white border border-zinc-200 rounded-xl overflow-hidden card-hover">
              <div className="aspect-video relative bg-zinc-100 overflow-hidden">
                {previewUrl ? (
                  <img
                    src={previewUrl}
                    alt={content.title}
                    className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                    loading="lazy"
                    onError={(e) => { e.currentTarget.style.display = 'none'; }}
                  />
                ) : (
                  <div className="w-full h-full flex items-center justify-center bg-gradient-to-br from-zinc-50 to-zinc-100">
                    {content.content_type === 'video' ? (
                      <div className="w-16 h-16 rounded-full bg-[#D4AF37]/20 flex items-center justify-center">
                        <Play className="w-8 h-8 text-[#D4AF37]" />
                      </div>
                    ) : (
                      <div className="w-16 h-16 rounded-full bg-[#D4AF37]/20 flex items-center justify-center">
                        <Download className="w-8 h-8 text-[#D4AF37]" />
                      </div>
                    )}
                  </div>
                )}

                <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity duration-300 flex items-center justify-center">
                  <div className="w-16 h-16 rounded-full bg-[#D4AF37] flex items-center justify-center">
                    {content.content_type === 'video' ? (
                      <div className="w-0 h-0 border-l-[16px] border-l-white border-y-[10px] border-y-transparent ml-1" />
                    ) : (
                      <Download className="w-6 h-6 text-white" />
                    )}
                  </div>
                </div>

                <div className="absolute top-3 left-3">
                  <span className={`px-2 py-1 text-xs font-medium rounded-full ${
                    mediaKind === 'video'
                      ? 'bg-[#D4AF37] text-white'
                      : 'bg-zinc-900/80 text-white'
                  }`}>
                    {contentKindLabel(mediaKind)}
                  </span>
                </div>

                <div
                  className="absolute bottom-2 right-2 w-8 h-8 rounded-full bg-white/95 shadow-md border border-zinc-200/80 flex items-center justify-center text-zinc-700 pointer-events-none"
                  aria-hidden
                >
                  <KindIcon className="w-4 h-4" strokeWidth={2} />
                </div>

                {isNew(content.created_at) && (
                  <div
                    className="absolute top-3 right-3"
                    data-testid={`content-new-badge-${content.id}`}
                  >
                    <span className="inline-flex items-center gap-1 px-2 py-1 text-[10px] font-semibold tracking-wide uppercase rounded-full bg-white/95 text-zinc-900 ring-1 ring-[#D4AF37] shadow-sm">
                      <span className="w-1.5 h-1.5 rounded-full bg-[#D4AF37] animate-pulse" />
                      Nuovo
                    </span>
                  </div>
                )}
              </div>

              <div className="p-4">
                <h4 className="text-zinc-900 font-medium truncate mb-1">{content.title}</h4>
                {content.description && (
                  <p className="text-zinc-500 text-sm line-clamp-2">{content.description}</p>
                )}
              </div>
            </div>
          </div>
          );
        })}
      </div>
    </div>
  );
};

export default ContentCarousel;
